import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/config";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Bell, BellDot, Check, Trash2, Loader2, Inbox, MailOpen, Calendar, CreditCard, PackagePlus, Star, Heart, UserCog, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const ICON_BY_CATEGORY = {
  booking: Calendar,
  payment: CreditCard,
  extra_work: PackagePlus,
  review: Star,
  tip: Heart,
  profile: UserCog,
  schedule: ClipboardList,
};
const TONE_BY_CATEGORY = {
  booking: "text-emerald-700 bg-emerald-100",
  payment: "text-violet-700 bg-violet-100",
  extra_work: "text-amber-700 bg-amber-100",
  review: "text-yellow-700 bg-yellow-100",
  tip: "text-pink-700 bg-pink-100",
  profile: "text-slate-700 bg-slate-100",
  schedule: "text-indigo-700 bg-indigo-100",
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(1, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Persistent Notification Center. Works for both admin and customer — the
 * backend returns notifications scoped to the current user_id.
 * Props:
 *   className: extra classes for the trigger button
 */
export default function NotificationCenter({ className = "" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data?.unread_count || 0);
    } catch (_e) { /* silent — user not logged in */ }
  }, []);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications?limit=100");
      setList(data?.notifications || []);
      setUnread(data?.unread_count || 0);
    } catch (_e) {
      setList([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 30_000);   // poll every 30s
    return () => clearInterval(id);
  }, [refreshCount]);

  useEffect(() => { if (open) refreshList(); }, [open, refreshList]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-read", { ids: null });
      setList((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch (_e) { toast.error("Could not mark all as read"); }
  };
  const clearAll = async () => {
    try {
      await api.post("/notifications/clear-all");
      setList([]); setUnread(0);
    } catch (_e) { toast.error("Could not clear"); }
  };
  const openOne = async (n) => {
    // Optimistic mark-read + navigate
    if (!n.read_at) {
      try { await api.post(`/notifications/${n.notif_id}/read`); } catch (_e) { /* ignore */ }
      setList((prev) => prev.map((x) => x.notif_id === n.notif_id ? { ...x, read_at: new Date().toISOString() } : x));
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    if (n.deep_link) navigate(n.deep_link);
  };
  const removeOne = async (e, id) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}`);
      setList((prev) => prev.filter((x) => x.notif_id !== id));
    } catch (_e) { toast.error("Could not delete"); }
  };

  const BellIcon = unread > 0 ? BellDot : Bell;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button type="button" data-testid="notification-bell"
                className={`relative p-2 rounded-full hover:bg-emerald-100 transition-colors ${className}`}>
          <BellIcon className={`w-5 h-5 ${unread > 0 ? "text-emerald-700" : "text-gray-700"}`} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-extrabold flex items-center justify-center"
                  data-testid="notification-unread-badge">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="sm:max-w-md p-0 flex flex-col" data-testid="notification-center">
        <SheetHeader className="p-4 border-b flex-row items-center justify-between space-y-0">
          <div>
            <SheetTitle className="text-xl font-extrabold tracking-tight font-[Outfit]">
              Notifications
            </SheetTitle>
            <SheetDescription className="text-xs">
              {unread > 0 ? `${unread} unread` : "All caught up"}
            </SheetDescription>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={markAllRead} disabled={unread === 0}
                    className="text-[11px] font-bold" data-testid="notif-mark-all-read">
              <MailOpen className="w-3.5 h-3.5 mr-1" /> Mark all read
            </Button>
            <Button variant="ghost" size="sm" onClick={clearAll} disabled={list.length === 0}
                    className="text-[11px] font-bold text-red-600" data-testid="notif-clear-all">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Clear
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>
          ) : list.length === 0 ? (
            <div className="p-10 text-center text-gray-500" data-testid="notif-empty">
              <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm font-semibold">No notifications yet.</p>
              <p className="text-xs mt-1">Bookings, payments, and messages will show up here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {list.map((n) => {
                const Icon = ICON_BY_CATEGORY[n.category] || Bell;
                const tone = TONE_BY_CATEGORY[n.category] || "text-gray-700 bg-gray-100";
                return (
                  <li key={n.notif_id}
                      onClick={() => openOne(n)}
                      data-testid={`notif-item-${n.notif_id}`}
                      className={`p-3 flex gap-3 items-start cursor-pointer transition-colors ${n.read_at ? "bg-white hover:bg-gray-50" : "bg-emerald-50/60 hover:bg-emerald-50"}`}>
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${tone}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <p className={`text-[13px] truncate ${n.read_at ? "font-semibold text-gray-800" : "font-extrabold text-gray-900"}`}>
                          {n.title}
                        </p>
                        <span className="text-[10px] text-gray-500 flex-shrink-0">{timeAgo(n.created_at)}</span>
                      </div>
                      <p className="text-[12px] text-gray-600 line-clamp-2">{n.body}</p>
                    </div>
                    <button onClick={(e) => removeOne(e, n.notif_id)}
                            className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-600 flex-shrink-0"
                            data-testid={`notif-delete-${n.notif_id}`} title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
